import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface Appointment {
  id: string;
  date: string;
  timeSlot: string;
  clientName: string;
  clientPhone: string | null;
  vehiclePlate: string;
  vehicleName: string;
  serviceDescription: string;
  status: string;
  createdAt: string;
}

interface AppointmentFilters {
  status?: string;
  from?: string;
  to?: string;
}

export function useAppointments(filters: AppointmentFilters = {}) {
  return useQuery<{ appointments: Appointment[] }>({
    queryKey: ['appointments', filters],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (filters.status) params.status = filters.status;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      const res = await api.get('/workshops/my/appointments', { params });
      return res.data;
    },
  });
}

export function useAppointmentCount() {
  return useQuery<number>({
    queryKey: ['appointments', 'pending-count'],
    queryFn: async () => {
      const res = await api.get('/workshops/my/appointments', {
        params: { status: 'pending' },
      });
      return (res.data.appointments ?? []).length;
    },
    refetchInterval: 30_000,
  });
}

export function useAppointmentActions() {
  const queryClient = useQueryClient();

  const confirm = useMutation({
    mutationFn: async (id: string) => {
      await api.patch(`/workshops/my/appointments/${id}/confirm`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });

  const cancel = useMutation({
    mutationFn: async (id: string) => {
      await api.patch(`/workshops/my/appointments/${id}/cancel`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });

  return { confirm, cancel };
}
